import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../utils/utils';
import Swal from 'sweetalert2';

interface SharingNotification {
    id: string;
    medical_record: {
        id: string;
        name: string;
        record_identifier: string;
        chief_complaint: string;
        date_of_service: string;
        clinic: {
            id: number;
            name: string;
        };
    };
    consented_to: {
        id: number;
        name: string;
        email: string;
    };
    status: 'pending' | 'active' | 'rejected' | 'expired';
    purpose: string;
    scope: string;
    created_at: string;
    updated_at: string;
    start_date?: string;
    end_date?: string;
}

interface NotificationStats {
    pending: number;
    active: number;
    total: number;
}

const SharingNotifications: React.FC = () => {
    const [notifications, setNotifications] = useState<SharingNotification[]>([]);
    const [stats, setStats] = useState<NotificationStats>({ pending: 0, active: 0, total: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const base_url = useBaseUrl();

    useEffect(() => {
        loadNotifications();
        // Set up real-time polling
        const interval = setInterval(loadNotifications, 15000); // Poll every 15 seconds
        return () => clearInterval(interval);
    }, []);

    const loadNotifications = async () => {
        try {
            const patientId = localStorage.getItem('patient_id');
            const patientToken = localStorage.getItem('patient_token');
            
            if (!patientId || !patientToken) {
                setError("Authentication required");
                return;
            }

            const authHeaders = {
                'Content-Type': 'application/json',
                'X-Patient-ID': patientId.toString(),
                'Authorization': `Bearer ${patientToken}`
            };

            // Load all consent requests for this patient
            const response = await axios.get(
                `${base_url}ehr/api/v1/share-requests/`,
                { headers: authHeaders }
            );

            if (response.data.status === "success") {
                const allNotifications = response.data.data || [];
                setNotifications(allNotifications);
                
                // Calculate stats
                const pendingCount = allNotifications.filter((n: SharingNotification) => n.status === 'pending').length;
                const activeCount = allNotifications.filter((n: SharingNotification) => n.status === 'active').length;
                
                setStats({
                    pending: pendingCount,
                    active: activeCount,
                    total: allNotifications.length
                });
                
                // Show notification for new pending requests
                if (pendingCount > 0) {
                    showNewRequestNotification(pendingCount);
                }
            }

            setError(null);
        } catch (error: any) {
            console.error("Error loading notifications:", error);
            setError("Failed to load sharing notifications");
        } finally {
            setIsLoading(false);
        }
    };

    const showNewRequestNotification = (count: number) => {
        // Only show if there are new pending requests and this isn't the initial load
        if (!isLoading && count > 0) {
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
            });

            Toast.fire({
                icon: 'info',
                title: `You have ${count} pending sharing request${count > 1 ? 's' : ''}`,
                text: 'Please review and respond to the requests'
            });
        }
    };

    const quickApprove = async (notificationId: string) => {
        try {
            const patientId = localStorage.getItem('patient_id');
            const patientToken = localStorage.getItem('patient_token');
            
            if (!patientId || !patientToken) {
                throw new Error("Authentication required");
            }

            const authHeaders = {
                'Content-Type': 'application/json',
                'X-Patient-ID': patientId.toString(),
                'Authorization': `Bearer ${patientToken}`
            };

            const response = await axios.post(
                `${base_url}ehr/api/v1/share-requests/${notificationId}/approve_request/`,
                {},
                { headers: authHeaders }
            );

            if (response.data.status === "success") {
                await Swal.fire({
                    icon: 'success',
                    title: 'Request Approved',
                    text: 'The sharing request has been approved successfully',
                    timer: 2000
                });
                loadNotifications(); // Refresh the list
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to approve request'
            });
        }
    };

    const quickReject = async (notificationId: string) => {
        try {
            const { value: reason } = await Swal.fire({
                title: 'Reject Sharing Request',
                input: 'textarea',
                inputLabel: 'Reason for rejection (optional):',
                inputPlaceholder: 'Enter your reason...',
                showCancelButton: true,
                confirmButtonText: 'Reject',
                confirmButtonColor: '#dc3545'
            });

            if (reason !== undefined) {
                const patientId = localStorage.getItem('patient_id');
                const patientToken = localStorage.getItem('patient_token');
                
                if (!patientId || !patientToken) {
                    throw new Error("Authentication required");
                }

                const authHeaders = {
                    'Content-Type': 'application/json',
                    'X-Patient-ID': patientId.toString(),
                    'Authorization': `Bearer ${patientToken}`
                };

                const response = await axios.post(
                    `${base_url}ehr/api/v1/share-requests/${notificationId}/reject_request/`,
                    { reason: reason || 'Rejected by patient' },
                    { headers: authHeaders }
                );

                if (response.data.status === "success") {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Request Rejected',
                        text: 'The sharing request has been rejected',
                        timer: 2000
                    });
                    loadNotifications(); // Refresh the list
                }
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to reject request'
            });
        }
    };

    const getStatusBadge = (status: string) => {
        const badges = {
            'pending': 'bg-warning text-dark',
            'active': 'bg-success text-white',
            'rejected': 'bg-danger text-white',
            'expired': 'bg-secondary text-white'
        };
        return badges[status as keyof typeof badges] || 'bg-secondary text-white';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    if (isLoading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading notifications...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            <style>{`
                .notification-card {
                    border: none;
                    border-radius: 12px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
                    transition: all 0.3s ease;
                    margin-bottom: 1rem;
                }
                
                .notification-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.12);
                }
                
                .stats-badge {
                    border-radius: 10px;
                    padding: 0.5rem 1rem;
                    margin: 0.25rem;
                    display: inline-block;
                }
                
                .action-btn {
                    border-radius: 8px;
                    padding: 0.4rem 1rem;
                    margin: 0.2rem;
                    transition: all 0.2s ease;
                }
                
                .action-btn:hover {
                    transform: scale(1.05);
                }
                
                .urgent-notification {
                    border-left: 5px solid #ff6b6b;
                    background: linear-gradient(90deg, #fff5f5 0%, #ffffff 100%);
                }
                
                .active-notification {
                    border-left: 5px solid #51cf66;
                    background: linear-gradient(90deg, #f3f9f3 0%, #ffffff 100%);
                }
            `}</style>

            {/* Header with Stats */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-0">🔔 Sharing Notifications</h2>
                    <p className="text-muted">Manage your medical record sharing requests</p>
                </div>
                <div>
                    <span className="stats-badge bg-warning text-dark">
                        <strong>{stats.pending}</strong> Pending
                    </span>
                    <span className="stats-badge bg-success text-white">
                        <strong>{stats.active}</strong> Active
                    </span>
                    <span className="stats-badge bg-primary text-white">
                        <strong>{stats.total}</strong> Total
                    </span>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                </div>
            )}

            {/* Notifications List */}
            {notifications.length === 0 ? (
                <div className="card notification-card">
                    <div className="card-body text-center py-5">
                        <h4 className="text-muted">📭 No Sharing Requests</h4>
                        <p className="text-muted">You don't have any medical record sharing requests at this time</p>
                    </div>
                </div>
            ) : (
                <div>
                    {notifications.map((notification) => (
                        <div 
                            key={notification.id} 
                            className={`card notification-card ${
                                notification.status === 'pending' ? 'urgent-notification' : 
                                notification.status === 'active' ? 'active-notification' : ''
                            }`}
                        >
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <div className="d-flex align-items-center mb-2">
                                            <h5 className="mb-0 me-3">
                                                📄 {notification.medical_record.name}
                                            </h5>
                                            <span className={`badge ${getStatusBadge(notification.status)}`}>
                                                {notification.status.toUpperCase()}
                                            </span>
                                        </div>
                                        
                                        <p className="mb-1">
                                            <strong>🏥 Requesting Facility:</strong> {notification.consented_to.name}
                                        </p>
                                        <p className="mb-1">
                                            <strong>📋 Record ID:</strong> {notification.medical_record.record_identifier}
                                        </p>
                                        <p className="mb-1">
                                            <strong>🎯 Purpose:</strong> {notification.purpose}
                                        </p>
                                        <p className="mb-2">
                                            <strong>📅 Requested:</strong> {formatDate(notification.created_at)}
                                        </p>
                                        
                                        {notification.scope && (
                                            <details className="mb-2">
                                                <summary className="btn btn-link p-0 text-decoration-none">
                                                    <small>📝 View Scope Details</small>
                                                </summary>
                                                <div className="mt-2 p-2 bg-light rounded">
                                                    <small>{notification.scope}</small>
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                    
                                    <div className="col-md-4 text-end">
                                        {notification.status === 'pending' && (
                                            <div>
                                                <button 
                                                    className="btn btn-success action-btn"
                                                    onClick={() => quickApprove(notification.id)}
                                                >
                                                    ✅ Approve
                                                </button>
                                                <button 
                                                    className="btn btn-danger action-btn"
                                                    onClick={() => quickReject(notification.id)}
                                                >
                                                    ❌ Reject
                                                </button>
                                            </div>
                                        )}
                                        
                                        {notification.status === 'active' && (
                                            <div>
                                                <p className="mb-1">
                                                    <small className="text-success">
                                                        ✅ Active since: {formatDate(notification.start_date || notification.updated_at)}
                                                    </small>
                                                </p>
                                                {notification.end_date && (
                                                    <p className="mb-0">
                                                        <small className="text-warning">
                                                            ⏰ Expires: {formatDate(notification.end_date)}
                                                        </small>
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        
                                        {notification.status === 'rejected' && (
                                            <p className="mb-0">
                                                <small className="text-danger">
                                                    ❌ Rejected on: {formatDate(notification.updated_at)}
                                                </small>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SharingNotifications;
